import React from 'react';

// external libs

import { Navigate, Route, Routes } from 'react-router-dom';

// common

import PrivateRoute from '@components/navigation/PrivateRoute';
import CapHistoricalRouteGuard from '@components/navigation/CapHistoricalRouteGuard';
import { configs } from '@constants/configs/configs';
import states from '@constants/configs/states';
import { ALL_USER_ROLES } from '@constants/roles/userRoleTypes';
import RouteRenderEnvironment from './RouteRenderEnvironment';

// components

import AccessCodeContainer from '@components/accessCode/AccessCodeContainer';
import WithRecaptcha from '@components/common/form/recaptcha/WithRecaptcha';
import ForgotEmailContainer from '@components/anonymous/forgotEmail/ForgotEmailContainer';
import HomeContainer from '@components/anonymous/home/HomeContainer';
import PreOEPLandingContainer from '@components/anonymous/pre-OEP_landing/pre-oep_landingContainer';
import EDESearchAndSelfDesignationContainer from '@components/broker/edeSearchAndDesignation/EDESearchAndSelfDesignationContainer';
import UpcomingDueDateContainer from '@components/charts/UpcomingDueDateContainer';
import SecureInbox from '@components/individualPortal/secureInbox/SecureInbox';
import BrokerConnectContainer from '@containers/BrokerConnectContainer/BrokerConnectContainer';
import IdProofingContainer from '@components/broker/edeSearchAndDesignation/idProofing/IdProofingContainer';
import EDESearchContainer from '@components/broker/edeSearchAndDesignation/search/EDESearchContainer';
import BrokerContainer from '@containers/broker/BrokerContainer';
import CrmContainer from '@containers/crm/CrmContainer';
import DelegationContainer from '@containers/delegation/DelegationContainer';
import DocumentsVerificationContainer from '@containers/documentsVerificationContainer/DocumentsVerificationContainer';
import EntityExportContainer from '@containers/entity/EntityExportContainer';
import EntityTransportBOBContainer from '@containers/entity/EntityTransportBOBContainer';
import FallbackContainer from '@containers/fallback/FallbackContainer';
import CommunicationPreferencesContainer from '@containers/individualPortal/CommunicationPreferencesContainer';
import IndividualPortalContainer from '@containers/individualPortal/IndividualPortalContainer';
import TobaccoContainer from '@containers/individualPortal/tobacco/TobaccoContainer';
import HistoricalDocumentsContainer from '@modules/historical-documents/member/HistoricalDocumentsContainer';
import HistoricalNoticesContainer from '@modules/historical-notices/member/HistoricalNoticesContainer';
import CapHistoricalDocumentsContainer from '@modules/historical-documents/cap/CapHistoricalDocumentsContainer';
import CapHistoricalDocumentsAdminLayout from '@modules/historical-documents/cap/CapHistoricalDocumentsAdminLayout';
import CapHistoricalNoticesContainer from '@modules/historical-notices/cap/CapHistoricalNoticesContainer';
import CapHistoricalNoticesAdminLayout from '@modules/historical-notices/cap/CapHistoricalNoticesAdminLayout';
import CapHistoricalDocumentsLayout from '@modules/historical-documents/cap/CapHistoricalDocumentsLayout';
import CapHistoricalNoticesLayout from '@modules/historical-notices/cap/CapHistoricalNoticesLayout';
import LceContainer from '@containers/lce/LceContainer';
import ApplicationErrorBoundary from '@components/common/ApplicationErrorBoundary';
import SsapContainer from '@containers/ssap/SsapContainer';
import TicketMgmtContainer from '@containers/ticketMgmtPortal/TicketMgmtContainer';
import TicketSendMessageContainer from '@containers/ticketMgmtPortal/sendMessage/TicketSendMessageContainer';
import TicketTypesManager from '@containers/ticketMgmtPortal/TicketTypesManager';
import TicketTypes from '@containers/ticketMgmtPortal/TicketTypes';
import TagsManagement from '@containers/ticketMgmtPortal/manage-tags/TagsManagement';
import CustomFieldManagement from '@containers/ticketMgmtPortal/manage-custom-fields/CustomFieldManagement';
import ExchangeAssigneesManagement from '@containers/ticketMgmtPortal/manage-assignees/ExchangeAssigneesManagment';
import RIDPContainer from '@modules/ridp/index';
import CapApplicationsContainer from '@modules/members/applications';
import AdminSerffStatusContainer from '@modules/admin/serff';
import BrokerTicketListContainer from '@containers/broker/ticket/BrokerTicketListContainer';
import TicketSummaryContainer from '@containers/individualPortal/ticket/TicketSummaryContainer';
import GetStarted from '@modules/ridp/components/Steps/GetStarted';
import ContactInformation from '@modules/ridp/components/Steps/ContactInformation';
import IdentityQuestions from '@modules/ridp/components/Steps/IdentityQuestions';
import CallExperian from '@modules/ridp/components/Steps/CallExperian';
import ManualVerification from '@modules/ridp/components/Steps/ManualVerification';
import Finish from '@modules/ridp/components/Steps/Finish';
import UploadDocumentProcess from '@modules/ridp/components/Steps/UploadDocumentProcess';
import EnrollmentHistory from '@modules/members/enrollments/EnrollmentListing/EnrollmentHistory';
import EnrollmentPremiumContainer from '@modules/members/enrollments/EnrollmentPremium/EnrollmentPremiumContainer';
import Participation from '@containers/BrokerConnectContainer/Participation';
import Availability from '@containers/BrokerConnectContainer/Availability';
import DashboardContainer from '@containers/individualPortal/dashboard/DashboardContainer';
import TobaccoExemptContainer from '@modules/tobacco-exempt';
import ApplicationsContainer from '@containers/individualPortal/applications/ApplicationsContainer';
import EligibilityResultsContainer from '@containers/individualPortal/eligibilityResults/EligibilityResultsContainer';
import CustomGroupingContainer from '@modules/custom-grouping';
import MyEnrollmentHistoryContainer from '@containers/individualPortal/myEnrollment/MyEnrollmentHistoryContainer';
import TicketContainer from '@containers/individualPortal/ticket/TicketContainer';
import MultiHouseholdDetails from '@containers/individualPortal/multiHouseholdDetails/index';
import ManageWorkgroupContainer from '@modules/manageworkgroup/ManageWorkgroupContainer';
import { useUserPermissions } from '@hooks/useUserPermissions';
import { ResetPasswordContainer, ResetPasswordForm, ResetPasswordSuccess } from '@modules/resetPassword';
import AdminProvision from '../modules/admin/provision/index';

import ArchiveHouseholdContainer from '@modules/archive-household';
import { IssuerEdiTestingContainer, IssuerEdiTestingDetailsContainer } from '@modules/issuer-edi-testing';
import { SecureInboxContainer } from '@modules/secure-inbox';
// types

import type { STATES_TYPE, IIsState } from '@constants/configs/states';

// Providers

import { getRouteAndState } from '@components/anonymous/home/utils';
import ApplicationEligibility from '@modules/crm-application-eligibility/ApplicationEligibility';
import VerificationsContainer from '@modules/dmi-verifications';
import AddMemberContainer from '@modules/members/add-member/AddMemberContainer';
import EnrollmentContainer from '@modules/members/enrollments/EnrollmentContainer';
import AccountActivityContainer from '@modules/members/account-activity/AccountActivityContainer';
import { TicketListContainer } from '@modules/cap/TicketList/TicketListContainer';
import FeaturePlanContainer from '@modules/issuer-feature-plan/FeaturePlanContainer';
import LoadPlans from '@modules/plans/load-plans/LoadPlans';
import LoadDrugs from '@modules/plans/load-drugs/LoadDrugs';

import MemberApplicationsListContainer from '@modules/members/applications/appContainer/MemberApplicationsListContainer';
import AdminSerffPlansLoadStatusContainer from '@modules/admin/serff/AdminSerffPlansLoadStatus';
import AdminSerffTransferStatusContainer from '@modules/admin/serff/AdminSerffTransferStatus';
import ManageDrugsContainer from '@modules/admin/manage-drugs';
import PlanSummaryContainer from '@containers/individualPortal/planSummary/PlanSummaryContainer';
import ManageWorkgroupTagContainer from '@containers/ticketMgmtPortal/manageWorkgroupTags/ManageWorkgroupTagContainer';
import TemplateList from '@containers/ticketMgmtPortal/manageCannedResponse/TemplateList';
import QLEDocumentsContainer from '@modules/qle/QleMnContainer';
import SecurityQuestionContainer from '@modules/referral/dynamicSecurityQuestions';
import VoterRegistrationContainer from '@modules/voterRegistration/VoterRegistrationContainer';
import MediaAccessContainer from '@modules/cap/DataSync/MediaAccessContainer';
import MediaLayout from '@modules/cap/DataSync';
import {
    HistoricalApplicationsContainer,
    HistoricalBasicInfoPage,
    HistoricalApplicationsPage,
    HistoricalEnrollmentPage,
    HistoricalIncorrectReferenceIdPage,
} from '@modules/members/historical-applications';

interface Props {
    isState: IIsState;
    stateCode: STATES_TYPE;
    ticketEnabled: boolean;
    leadPresent: string | null;
    isMNFullStack: boolean;
    isStateFullStack: boolean;
    userData: {
        role: string;
        activeModuleName: string;
    };
    showHistoricalData?: boolean;
    historicalMigratedUser?: boolean;
}

const BROKER_CONNECT_STATES = [
    states.NEVADA,
    states.PENNSYLVANIA,
    states.VIRGINIA,
    states.GEORGIA,
    states.NEW_MEXICO,
    states.ILLINOIS,
    states.GETINSURED,
    states.OREGON,
];

const PRE_OEP_LANDING_STATES = [
    states.GEORGIA,
    states.VIRGINIA,
    states.ILLINOIS,
    states.NEW_MEXICO,
    states.NEVADA,
    states.MINNESOTA,
    states.IDAHO,
    states.NEW_JERSEY,
    states.GETINSURED,
    states.OREGON,
];

const HOME_PAGE_ALLOWED_STATES = [
    states.GEORGIA,
    states.IDAHO,
    states.NEVADA,
    states.NEW_JERSEY,
    states.PENNSYLVANIA,
    states.VIRGINIA,
    states.NEW_MEXICO,
    states.ILLINOIS,
    states.GETINSURED,
    states.OREGON,
];

const VOTER_REGISTRATION_STATES = [states.PENNSYLVANIA];

function AppRoutes(props: Props) {
    const {
        routes: {
            ACCESS_CODE,
            ARCHIVE_HOUSEHOLD,
            BROKER_CONNECT,
            BROKER_CONNECT_PARTICIPATION,
            BROKER_CONNECT_AVAILABILITY,
            COMMUNICATION_PREFERENCES_AC,
            CRM_APPLICATION_ELIGIBILITY,
            CRM_SEARCH,
            DELEGATION_OTHER,
            EDE_ID_PROOFING,
            EDE_SEARCH_PAGE,
            MEMBER_ENROLLMENT,
            MEMBER_ENROLLMENT_LIST,
            MEMBER_ENROLLMENT_LIST_PREMIUM_HISTORY,
            MEMBER_ENROLLMENT_PREMIUM_HISTORY,
            ENTITY_TRANSFER_BOB,
            ENTITY_EXPORT_BOB,
            FALLBACK,
            FORGOT_EMAIL,
            HOME,
            INDPORTAL,
            APPLICATIONS,
            COMMUNICATION_PREFERENCES,
            DELEGATION_CONSUMER,
            ELIGIBILITY_RESULTS,
            MULTI_HOUSEHOLD_DETAILS,
            TICKET_CONSUMER,
            TICKET_CONSUMER_DETAIL,
            CUSTOM_GROUPING,
            MY_ENROLLMENT_HISTORY,
            TOBACCO_EXEMPT,
            ISSUER_EDI_TESTING,
            ISSUER_EDI_TESTING_TEST_DETAILS,
            ISSUER_FEATURE_PLAN,
            LCE,
            NEW_INBOX_HOME,
            HISTORICAL_DOCUMENTS,
            HISTORICAL_NOTICES,
            PRE_OEP_LANDING,
            REPORT_DUEDATE_CHARTS,
            RIDP,
            RIDP_CONTACT_INFO,
            RIDP_IDENTITY_QUESTION,
            RIDP_CALL_EXPERIAN,
            RIDP_UPLOAD_DOCUMENT,
            RIDP_VERIFICATION_REJECTED,
            RIDP_TECHNICAL_ERROR,
            RIDP_VERIFICATION_COMPLETE,
            RIDP_UPLOAD_DOCUMENT_PROCESS,
            SECURE_INBOX,
            ACCOUNT_ACTIVITY,
            SSAP,
            TICKET_BROKER,
            TICKET_BROKER_DETAIL,
            TICKET_MGMT,
            TICKET_MGMT_TYPES,
            TICKET_SEND_MESSAGE,
            TICKET_MGMT_EXCHANGE_ASSIGNEES,
            TICKET_MGMT_MANAGE_TAGS,
            TICKET_MGMT_MANAGE_CUSTOM_FIELDS,
            TICKET_MGMT_WORKGROUP_TAG_EDIT,
            TICKET_MGMT_MANAGE_CANNED_RESPONSES,
            TOBACCO,
            VERIFICATION_DOCUMENTS,
            ADD_MEMBER,
            DMI_VERIFICATIONS,
            CAP_APPLICATIONS,
            CAP_MEMBER_HISTORICAL_APPLICANTS,
            CAP_MEMBER_HISTORICAL_APPLICATIONS,
            CAP_MEMBER_HISTORICAL_ENROLLMENTS,
            CAP_MEMBER_HISTORICAL_DOCUMENTS,
            CAP_MEMBER_HISTORICAL_NOTICES,
            CAP_MEMBER_HISTORICAL_REFERENCE_ID_NOT_FOUND,
            ADMIN_SERFF,
            ADMIN_SERFF_PLANS_LOAD_STATUS,
            ADMIN_SERFF_TRANSFER_STATUS,
            TICKET_LIST,
            LOAD_PLANS,
            LOAD_DRUGS,
            CAP_APPLICATIONS_SSAP_SUMMARY,
            MANAGE_DRUGS,
            DYNAMIC_SECURITY_QUESTIONS,
            PLAN_SUMMARY,
            TICKET_MGMT_MANAGE_WORKGROUP,
            RESET_PASSWORD,
            RESET_PASSWORD_SUCCESS,
            ADMIN_PROVISION,
            QLE_DOCUMENTS_MN,
            VOTER_REGISTRATION,
            MEDIA_ACCESS,
        },
    } = configs;

    const {
        isState,
        isMNFullStack,
        stateCode,
        ticketEnabled,
        userData,
        leadPresent,
        showHistoricalData = false,
        historicalMigratedUser = false,
    } = props;

    const isBrokerConnectAllowed = BROKER_CONNECT_STATES.includes(stateCode) || (isState?.MN && isMNFullStack);
    const isPreOEPLandingAllowed = PRE_OEP_LANDING_STATES.includes(stateCode);
    const isHomePageAllowed = HOME_PAGE_ALLOWED_STATES.includes(stateCode) || isMNFullStack;
    const isVoterRegistrationAllowed = VOTER_REGISTRATION_STATES.includes(stateCode) || (isState?.MN && isMNFullStack);
    const canViewMemberPortal = userData?.activeModuleName.toUpperCase() === ALL_USER_ROLES.INDIVIDUAL;
    const updatedPath = getRouteAndState(leadPresent, null, stateCode);
    const REDIRECTED_LANDING_PAGE_PATH = updatedPath.route;
    const activeModuleName = userData?.activeModuleName?.toUpperCase();
    const canViewHistoricalData = canViewMemberPortal && showHistoricalData && historicalMigratedUser;

    const mediaAccessAllowedRoles = [ALL_USER_ROLES.ADMIN, ALL_USER_ROLES.SUPERVISOR];
    const isMediaAccessAllowed = mediaAccessAllowedRoles.includes(activeModuleName);

    // ADMIN_SERFF parent layout is reachable by ADMIN or OPERATIONS; the actual SERFF
    // status pages underneath are further restricted to OPERATIONS (see below).
    const adminOrOperationsRoles = [ALL_USER_ROLES.ADMIN, ALL_USER_ROLES.OPERATIONS];
    const isForAdminOrOperations = adminOrOperationsRoles.includes(activeModuleName);

    // Gate OPERATIONS-only admin pages so other roles cannot reach them via direct URL.
    // Covered routes:
    //   - SERFF Plans Load Status / Transfer Status (children of ADMIN_SERFF) — pen-test fix
    //   - SERFF Load Plans / Load Drugs — pen-test fix
    //   - Manage Drugs
    // Backend defense-in-depth (broader than OPERATIONS-only — tighten separately if required):
    //   - SERFF Plans Load Status / Transfer Status: PlanDataUploadController.loadPlansStatusPage / loadSERFFTransferStatusPage @PreAuthorize MANAGE_QHP_PLANS_PERMISSION (ADMIN_PORTAL_MANAGE_PLAN)
    //   - SERFF Load Plans / Load Drugs: PlanDataUploadController @PreAuthorize ADMIN_PORTAL_MANAGE_PLAN
    //   - Manage Drugs: AdminIssuerController.getDrugDataList @PreAuthorize MANAGE_ISSUER_PERMISSION_STRING
    const operationsOnlyAllowedRoles = [ALL_USER_ROLES.OPERATIONS];
    const isOperationsOnlyAllowed = operationsOnlyAllowedRoles.includes(activeModuleName);

    const {
        canAccessTicketManagement,
        canManageWorkgroup,
        canViewTicketList,
        canViewReports,
        canManageTicketTypes,
        canManageTags,
        canManageExchangeAssignees,
        canManageCustomFields,
        canEditWorkgroupTag,
        canManageCannedResponses,
        canEditTicket,
        canAddNewConsumer,
    } = useUserPermissions();
    const enableFeaturedPlans = sessionStorage.getItem('enableFeaturedPlans')
        ? Boolean(sessionStorage.getItem('enableFeaturedPlans'))
        : true;

    const individualPortalRoutes = [
        { path: INDPORTAL, element: <DashboardContainer />, isAllowed: canViewMemberPortal },
        {
            path: TOBACCO_EXEMPT,
            element: <RouteRenderEnvironment component={TobaccoExemptContainer} />,
            isAllowed: canViewMemberPortal,
        },
        {
            path: APPLICATIONS,
            element: <RouteRenderEnvironment component={ApplicationsContainer} />,
            isAllowed: canViewMemberPortal,
        },
        {
            path: COMMUNICATION_PREFERENCES,
            element: <RouteRenderEnvironment component={CommunicationPreferencesContainer} />,
            isAllowed: canViewMemberPortal,
        },
        {
            path: DELEGATION_CONSUMER,
            element: <RouteRenderEnvironment component={DelegationContainer} />,
            isAllowed: canViewMemberPortal,
        },
        {
            path: HISTORICAL_DOCUMENTS,
            element: <RouteRenderEnvironment component={HistoricalDocumentsContainer} />,
            isAllowed: canViewHistoricalData,
        },
        {
            path: HISTORICAL_NOTICES,
            element: <RouteRenderEnvironment component={HistoricalNoticesContainer} />,
            isAllowed: canViewHistoricalData,
        },
        {
            path: ELIGIBILITY_RESULTS,
            element: <RouteRenderEnvironment component={EligibilityResultsContainer} />,
            isAllowed: canViewMemberPortal,
        },
        { path: MULTI_HOUSEHOLD_DETAILS, element: <MultiHouseholdDetails />, isAllowed: canViewMemberPortal },
        { path: CUSTOM_GROUPING, element: <CustomGroupingContainer />, isAllowed: canViewMemberPortal },
        { path: MY_ENROLLMENT_HISTORY, element: <MyEnrollmentHistoryContainer />, isAllowed: canViewMemberPortal },
        { path: TICKET_CONSUMER, element: <TicketContainer />, isAllowed: ticketEnabled },
        { path: TICKET_CONSUMER_DETAIL, element: <TicketSummaryContainer />, isAllowed: ticketEnabled },
        {
            path: PLAN_SUMMARY,
            element: <RouteRenderEnvironment component={PlanSummaryContainer} />,
            isAllowed: canViewMemberPortal,
        },
    ];

    return (
        <Routes>
            <Route path={VERIFICATION_DOCUMENTS} element={<DocumentsVerificationContainer />} />
            <Route path={ARCHIVE_HOUSEHOLD} element={<ArchiveHouseholdContainer />} />
            <Route
                path={HOME}
                element={
                    <PrivateRoute
                        path={HOME}
                        isAllowed={isHomePageAllowed}
                        element={<HomeContainer />}
                        redirectPath={REDIRECTED_LANDING_PAGE_PATH}
                    />
                }
            />
            <Route path={COMMUNICATION_PREFERENCES_AC} element={<RouteRenderEnvironment component={CommunicationPreferencesContainer} />} />
            <Route path={ACCESS_CODE} element={<WithRecaptcha><AccessCodeContainer /></WithRecaptcha>} />
            <Route path={FALLBACK} element={<FallbackContainer />} />
            <Route path={LCE} element={<LceContainer />} />
            <Route
                path={SSAP}
                element={
                    <ApplicationErrorBoundary>
                        <SsapContainer />
                    </ApplicationErrorBoundary>
                }
            />
            <Route path={DELEGATION_OTHER} element={<DelegationContainer />} />
            <Route path={TOBACCO} element={<TobaccoContainer />} />
            <Route path={CRM_SEARCH} element={<CrmContainer />} />
            <Route
                path={EDE_ID_PROOFING}
                element={<RouteRenderEnvironment component={EDESearchAndSelfDesignationContainer} />}
            >
                <Route path={EDE_ID_PROOFING} element={<IdProofingContainer />} />
                <Route path={EDE_SEARCH_PAGE} element={<EDESearchContainer />} />
            </Route>
            <Route path={FORGOT_EMAIL} element={<WithRecaptcha><ForgotEmailContainer /></WithRecaptcha>} />
            <Route
                path={TICKET_BROKER}
                element={<PrivateRoute path={TICKET_BROKER} isAllowed={ticketEnabled} element={<BrokerContainer />} />}
            >
                <Route
                    path={TICKET_BROKER}
                    element={
                        <PrivateRoute
                            path={TICKET_BROKER}
                            isAllowed={ticketEnabled}
                            element={<BrokerTicketListContainer />}
                        />
                    }
                />
                <Route path={TICKET_BROKER_DETAIL} element={<TicketSummaryContainer />} />
            </Route>
            <Route path={ENTITY_EXPORT_BOB} element={<EntityExportContainer />} />
            <Route path={ENTITY_TRANSFER_BOB} element={<EntityTransportBOBContainer />} />
            <Route
                path={NEW_INBOX_HOME}
                element={<RouteRenderEnvironment component={SecureInbox} key={NEW_INBOX_HOME} />}
            />
            <Route
                path={TICKET_MGMT}
                element={
                    <PrivateRoute
                        path={TICKET_MGMT}
                        isAllowed={canAccessTicketManagement}
                        element={<TicketMgmtContainer />}
                    />
                }
            >
                <Route
                    path={TICKET_SEND_MESSAGE}
                    element={
                        <PrivateRoute
                            path={TICKET_SEND_MESSAGE}
                            isAllowed={canEditTicket}
                            element={<TicketSendMessageContainer />}
                        />
                    }
                />
                <Route
                    path={TICKET_MGMT_EXCHANGE_ASSIGNEES}
                    element={
                        <PrivateRoute
                            path={TICKET_MGMT_EXCHANGE_ASSIGNEES}
                            isAllowed={canManageExchangeAssignees}
                            element={<ExchangeAssigneesManagement />}
                        />
                    }
                />
                <Route
                    path={TICKET_MGMT_MANAGE_TAGS}
                    element={
                        <PrivateRoute
                            path={TICKET_MGMT_MANAGE_TAGS}
                            isAllowed={canManageTags}
                            element={<TagsManagement />}
                        />
                    }
                />
                <Route
                    path={TICKET_MGMT_TYPES}
                    element={
                        <PrivateRoute
                            path={TICKET_MGMT_TYPES}
                            isAllowed={canManageTicketTypes}
                            element={<TicketTypesManager />}
                        />
                    }
                >
                    <Route
                        path={`${TICKET_MGMT_TYPES}/:ticketCategory/:ticketCategorySpanishName?`}
                        element={<TicketTypes />}
                    />
                </Route>
                <Route
                    path={TICKET_MGMT_MANAGE_CUSTOM_FIELDS}
                    element={
                        <PrivateRoute
                            path={TICKET_MGMT_MANAGE_CUSTOM_FIELDS}
                            isAllowed={canManageCustomFields}
                            element={<CustomFieldManagement />}
                        />
                    }
                />
                <Route
                    path={TICKET_MGMT_WORKGROUP_TAG_EDIT}
                    element={
                        <PrivateRoute
                            path={TICKET_MGMT_WORKGROUP_TAG_EDIT}
                            isAllowed={canEditWorkgroupTag}
                            element={<ManageWorkgroupTagContainer />}
                        />
                    }
                />
                <Route
                    path={TICKET_MGMT_MANAGE_CANNED_RESPONSES}
                    element={
                        <PrivateRoute
                            path={TICKET_MGMT_MANAGE_CANNED_RESPONSES}
                            isAllowed={canManageCannedResponses}
                            element={<TemplateList />}
                        />
                    }
                />
                <Route
                    path={TICKET_MGMT_MANAGE_WORKGROUP}
                    element={
                        <PrivateRoute
                            path={TICKET_MGMT_MANAGE_WORKGROUP}
                            isAllowed={canManageWorkgroup}
                            element={<ManageWorkgroupContainer />}
                        />
                    }
                />
            </Route>
            <Route path={SECURE_INBOX} element={<SecureInboxContainer />} />
            <Route path={ACCOUNT_ACTIVITY} element={<AccountActivityContainer />} />
            <Route
                path={BROKER_CONNECT}
                element={
                    <PrivateRoute
                        path={BROKER_CONNECT}
                        isAllowed={isBrokerConnectAllowed}
                        element={<BrokerConnectContainer />}
                    />
                }
            >
                <Route
                    path={BROKER_CONNECT_PARTICIPATION}
                    element={
                        <PrivateRoute
                            path={BROKER_CONNECT_PARTICIPATION}
                            isAllowed={isBrokerConnectAllowed}
                            element={<Participation />}
                        />
                    }
                />
                <Route
                    path={BROKER_CONNECT_AVAILABILITY}
                    element={
                        <PrivateRoute
                            path={BROKER_CONNECT_AVAILABILITY}
                            isAllowed={isBrokerConnectAllowed}
                            element={<Availability />}
                        />
                    }
                />
            </Route>
            <Route path={ISSUER_EDI_TESTING} element={<IssuerEdiTestingContainer />} />
            <Route path={ISSUER_EDI_TESTING_TEST_DETAILS} element={<IssuerEdiTestingDetailsContainer />} />
            <Route
                path={ISSUER_FEATURE_PLAN}
                element={
                    <PrivateRoute
                        path={ISSUER_FEATURE_PLAN}
                        isAllowed={enableFeaturedPlans}
                        element={<FeaturePlanContainer />}
                    />
                }
            />
            <Route
                path={INDPORTAL}
                element={
                    <PrivateRoute
                        path={INDPORTAL}
                        isAllowed={canViewMemberPortal}
                        element={<IndividualPortalContainer />}
                        redirectPath={FALLBACK}
                    />
                }
            >
                {individualPortalRoutes.map(({ path, element, isAllowed }) => (
                    <Route
                        key={path}
                        path={path}
                        element={
                            <PrivateRoute path={path} isAllowed={isAllowed} element={element} redirectPath={FALLBACK} />
                        }
                    />
                ))}
            </Route>
            <Route path={CRM_APPLICATION_ELIGIBILITY} element={<ApplicationEligibility />} />
            <Route
                path={PRE_OEP_LANDING}
                element={
                    <PrivateRoute
                        path={PRE_OEP_LANDING}
                        isAllowed={isPreOEPLandingAllowed}
                        element={<PreOEPLandingContainer />}
                    />
                }
            />
            <Route
                path={REPORT_DUEDATE_CHARTS}
                element={
                    <PrivateRoute
                        path={REPORT_DUEDATE_CHARTS}
                        isAllowed={canViewReports}
                        element={<UpcomingDueDateContainer />}
                    />
                }
            />
            <Route
                path={ADD_MEMBER}
                element={
                    <PrivateRoute
                        path={ADD_MEMBER}
                        isAllowed={canAddNewConsumer}
                        element={<AddMemberContainer />}
                        redirectPath={FALLBACK}
                    />
                }
            />
            <Route path={RIDP} element={<RouteRenderEnvironment component={RIDPContainer} />}>
                <Route path={RIDP} element={<GetStarted />} />
                <Route path={RIDP_CONTACT_INFO} element={<ContactInformation />} />
                <Route path={RIDP_IDENTITY_QUESTION} element={<IdentityQuestions />} />
                <Route path={RIDP_CALL_EXPERIAN} element={<CallExperian />} />
                <Route path={RIDP_UPLOAD_DOCUMENT} element={<ManualVerification />} />
                <Route path={RIDP_VERIFICATION_REJECTED} element={<ManualVerification />} />
                <Route path={RIDP_TECHNICAL_ERROR} element={<Finish />} />
                <Route path={RIDP_VERIFICATION_COMPLETE} element={<Finish />} />
                <Route path={RIDP_UPLOAD_DOCUMENT_PROCESS} element={<UploadDocumentProcess />} />
            </Route>
            <Route path={DMI_VERIFICATIONS} element={<VerificationsContainer />} />
            <Route path={MEMBER_ENROLLMENT} element={<EnrollmentContainer />}>
                <Route path={MEMBER_ENROLLMENT_LIST} element={<EnrollmentHistory />} />
                <Route path={MEMBER_ENROLLMENT_LIST_PREMIUM_HISTORY} element={<EnrollmentPremiumContainer />} />
                <Route path={MEMBER_ENROLLMENT_PREMIUM_HISTORY} element={<EnrollmentPremiumContainer />} />
            </Route>
            <Route path={CAP_APPLICATIONS} element={<CapApplicationsContainer />}>
                <Route path={CAP_APPLICATIONS} element={<MemberApplicationsListContainer />} />
                <Route
                    path={CAP_APPLICATIONS_SSAP_SUMMARY}
                    element={
                        <ApplicationErrorBoundary>
                            <SsapContainer fromCAP={true} />
                        </ApplicationErrorBoundary>
                    }
                />
            </Route>
            <Route
                path={`${CAP_MEMBER_HISTORICAL_DOCUMENTS}/:id`}
                element={<CapHistoricalRouteGuard element={<CapHistoricalDocumentsAdminLayout />} />}
            >
                <Route index element={<CapHistoricalDocumentsContainer />} />
            </Route>
            <Route
                path={CAP_MEMBER_HISTORICAL_DOCUMENTS}
                element={
                    <CapHistoricalRouteGuard element={<CapHistoricalDocumentsLayout />} />
                }
            >
                <Route index element={<CapHistoricalDocumentsContainer />} />
            </Route>
            <Route
                path={`${CAP_MEMBER_HISTORICAL_NOTICES}/:id`}
                element={<CapHistoricalRouteGuard element={<CapHistoricalNoticesAdminLayout />} />}
            >
                <Route index element={<CapHistoricalNoticesContainer />} />
            </Route>
            <Route
                path={CAP_MEMBER_HISTORICAL_NOTICES}
                element={
                    <CapHistoricalRouteGuard element={<CapHistoricalNoticesLayout />} />
                }
            >
                <Route index element={<CapHistoricalNoticesContainer />} />
            </Route>
            <Route path={CAP_MEMBER_HISTORICAL_APPLICANTS} element={<HistoricalApplicationsContainer />} >
                <Route index element={<HistoricalBasicInfoPage />} />
                <Route path={CAP_MEMBER_HISTORICAL_APPLICATIONS} element={<HistoricalApplicationsPage />} />
                <Route path={CAP_MEMBER_HISTORICAL_ENROLLMENTS} element={<HistoricalEnrollmentPage />} />
            </Route>
            <Route path={CAP_MEMBER_HISTORICAL_REFERENCE_ID_NOT_FOUND} element={<HistoricalIncorrectReferenceIdPage />} />
            <Route path={RESET_PASSWORD} element={<ResetPasswordContainer />}>
                <Route path={RESET_PASSWORD} element={<ResetPasswordForm />}></Route>
                <Route path={RESET_PASSWORD_SUCCESS} element={<ResetPasswordSuccess />}></Route>
            </Route>
            <Route
                path={ADMIN_SERFF}
                element={
                    <PrivateRoute
                        path={ADMIN_SERFF}
                        isAllowed={isForAdminOrOperations}
                        element={<AdminSerffStatusContainer />}
                        redirectPath={FALLBACK}
                    />
                }
            >
                <Route index element={<Navigate to={ADMIN_SERFF_PLANS_LOAD_STATUS} replace />} />
                <Route
                    path={ADMIN_SERFF_PLANS_LOAD_STATUS}
                    element={
                        <PrivateRoute
                            path={ADMIN_SERFF_PLANS_LOAD_STATUS}
                            isAllowed={isOperationsOnlyAllowed}
                            element={<AdminSerffPlansLoadStatusContainer />}
                            redirectPath={FALLBACK}
                        />
                    }
                />
                <Route
                    path={ADMIN_SERFF_TRANSFER_STATUS}
                    element={
                        <PrivateRoute
                            path={ADMIN_SERFF_TRANSFER_STATUS}
                            isAllowed={isOperationsOnlyAllowed}
                            element={<AdminSerffTransferStatusContainer />}
                            redirectPath={FALLBACK}
                        />
                    }
                />
            </Route>
            <Route path={ADMIN_PROVISION} element={<AdminProvision />} />
            <Route
                path={MANAGE_DRUGS}
                element={
                    <PrivateRoute
                        path={MANAGE_DRUGS}
                        isAllowed={isOperationsOnlyAllowed}
                        element={<ManageDrugsContainer />}
                        redirectPath={FALLBACK}
                    />
                }
            />
            <Route
                path={TICKET_LIST}
                element={
                    <PrivateRoute
                        path={TICKET_LIST}
                        isAllowed={canViewTicketList}
                        element={<RouteRenderEnvironment component={TicketListContainer} />}
                    />
                }
            />
            <Route
                path={LOAD_PLANS}
                element={
                    <PrivateRoute
                        path={LOAD_PLANS}
                        isAllowed={isOperationsOnlyAllowed}
                        element={<RouteRenderEnvironment component={LoadPlans} />}
                        redirectPath={FALLBACK}
                    />
                }
            />
            <Route
                path={LOAD_DRUGS}
                element={
                    <PrivateRoute
                        path={LOAD_DRUGS}
                        isAllowed={isOperationsOnlyAllowed}
                        element={<LoadDrugs />}
                        redirectPath={FALLBACK}
                    />
                }
            />
            <Route path={QLE_DOCUMENTS_MN} element={<QLEDocumentsContainer />} />
            <Route path={DYNAMIC_SECURITY_QUESTIONS} element={<WithRecaptcha><SecurityQuestionContainer /></WithRecaptcha>} />
            <Route
                path={VOTER_REGISTRATION}
                element={
                    <PrivateRoute
                        path={VOTER_REGISTRATION}
                        isAllowed={isVoterRegistrationAllowed}
                        element={<VoterRegistrationContainer />}
                    />
                }
            />
            <Route
                path={MEDIA_ACCESS}
                element={
                    <PrivateRoute
                        path={MEDIA_ACCESS}
                        isAllowed={isMediaAccessAllowed}
                        element={<MediaLayout />}
                        redirectPath={FALLBACK}
                    />
                }
            >
                <Route
                    path={MEDIA_ACCESS}
                    index
                    element={
                        <PrivateRoute
                            path={MEDIA_ACCESS}
                            isAllowed={isMediaAccessAllowed}
                            element={<MediaAccessContainer />}
                            redirectPath={FALLBACK}
                        />
                    }
                />
            </Route>
        </Routes>
    );
}

export default AppRoutes;
